import * as Sentry from '@sentry/react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { IS_CLOUD } from '@qality/common-config';

import ErrorReportingService from './ErrorReportingService';
import StorageOfUniqueServices from './StorageOfUniqueServices';

const GET_MESSAGE = 'axios GET';
const POST_MESSAGE = 'axios POST';
const PUT_MESSAGE = 'axios PUT';
const PATCH_MESSAGE = 'axios PATCH';
const DELETE_MESSAGE = 'axios DELETE';

export default {
  get(url: string): Promise<AxiosResponse> {
    return axios.get(url).catch((e) => {
      sentryCaptureEvent(e, GET_MESSAGE);
      throw e;
    });
  },
  post(
    url: string,
    data: unknown,
    config: AxiosRequestConfig<unknown>
  ): Promise<AxiosResponse> {
    return axios.post(url, data, config).catch((e) => {
      sentryCaptureEvent(e, POST_MESSAGE);
      throw e;
    });
  },
  put(
    url: string,
    data: unknown,
    config: AxiosRequestConfig<unknown>
  ): Promise<AxiosResponse> {
    return axios.put(url, data, config).catch((e) => {
      sentryCaptureEvent(e, PUT_MESSAGE);
      throw e;
    });
  },
  patch(
    url: string,
    data: unknown,
    config: AxiosRequestConfig<unknown>
  ): Promise<AxiosResponse> {
    return axios.patch(url, data, config).catch((e) => {
      sentryCaptureEvent(e, PATCH_MESSAGE);
      throw e;
    });
  },
  delete(
    url: string,
    config?: AxiosRequestConfig<unknown>
  ): Promise<AxiosResponse> {
    return axios.delete(url, config).catch((e) => {
      sentryCaptureEvent(e, DELETE_MESSAGE);
      throw e;
    });
  },
};

async function sentryCaptureEvent(e: AxiosError | Error, message: string) {
  if (!IS_CLOUD) {
    return;
  }

  await StorageOfUniqueServices.get('HostNameService').init();

  Sentry.withScope((scope) => {
    let errObject = e;
    const errorDetails = ErrorReportingService.getDetails(e, message);

    if (!(errObject instanceof Error)) {
      errObject = new Error(message);
      errObject.name = errorDetails;
      errObject.message = message;
    }

    // Tags should not contain spaces
    scope.setTag('errorType', message.replace(/\s/g, ''));
    scope.setTag(
      'hostname',
      StorageOfUniqueServices.get('HostNameService').baseUrl
    );

    if (e instanceof AxiosError) {
      const statusCode = e.response?.status;
      scope.setTag('responseCode', statusCode);
    }

    scope.setExtra('errorMessage', errorDetails);
    scope.setExtra('message', message);
    scope.setLevel('error');

    Sentry.captureException(errObject);
  });
}
